import React, { useMemo, useState } from 'react';

import { Alert } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { AsyncStatus } from 'enums';
import { UpdatePageModel } from '../UpdatePageModel';
import { IStage } from 'interfaces';

interface IDeleteStageModalProps {
    model: UpdatePageModel,
    show: boolean,
    stage: IStage,
    onCancel: () => void,
    onSuccess: () => void,
}

export function DeleteStageModal({ model, show, stage, onCancel, onSuccess }: IDeleteStageModalProps) {
    if (!model.update || !stage) return null;
    // We're memoing this so that it doesn't change to -1 after we delete it,
    // which could look weird to the user as the modal closes.
    const stageIndex = useMemo(() => {
        return model.update.rollout.stages.findIndex(s => s.id === stage.id);
    }, [stage]);
    const stageNum = stageIndex + 1;

    const [status, setStatus] = useState(AsyncStatus.Initial);
    const [err, setErr] = useState('');

    function tryHide() {
        // Prevent closing the modal if an API call is in progress.
        if (status !== AsyncStatus.Loading) {
            onCancel();
        }
    }

    function onConfirm() {
        if (status === AsyncStatus.Loading) return;

        setStatus(AsyncStatus.Loading);

        model.deleteStage(stage.id)
            .then(onSuccess)
            .catch(() => {
                setStatus(AsyncStatus.Error);
                setErr(model.apiCallError);
            });
    }

    return (
        <Modal show={show} onClose={tryHide}>
            <ModalHeader>
                Delete Stage {stageNum}
            </ModalHeader>
            <ModalBody>
                <p>
                    Are you sure you want to delete Stage {stageNum}?
                </p>
                {status === AsyncStatus.Error && (
                    <Alert type="danger">
                        We're sorry, an error has occurred: {err}
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="outlined"
                    onClick={tryHide}
                    disabled={status === AsyncStatus.Loading}
                >
                    Cancel
                </Button>
                <Button
                    style={{marginLeft: "10px"}}
                    onClick={onConfirm}
                    loading={status === AsyncStatus.Loading}
                    disabled={status === AsyncStatus.Success}
                >
                    {status === AsyncStatus.Loading && `Deleting stage ${stageNum}...`}
                    {status !== AsyncStatus.Loading && `Delete stage ${stageNum}`}
                </Button>
            </ModalFooter>
        </Modal>
    );
}